import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { TagModel } from '@notino/react-styleguide';
import { StockAvailability } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';
import { Tags } from '@components/Tags';
import { getRenderableAttributes } from '@components/Tags/utils';
import { useIsDualPriceForCroatia } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPriceForCroatia';
import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { VariantDisplayTypes } from '@containers/ProductListing/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dataLabelsProp } from '@helpers/utils';

import { IVariantProps } from '../../../../model';
import { trackVariantClick } from '../../tracking';
import {
  VariantListItem,
  VariantLink,
  PriceLabelCurrency,
  Name,
} from '../../variantsStyled';

import { messages } from './messages';
import { Variant, PriceLabelWrapper, Unavailable } from './styled';
import { trackTagClick } from './tracking';

export const VariantTile: React.FC<IVariantProps> = ({
  item,
  isSelected,
  unit,
}) => {
  const { getTimeFromInit } = useTrackingContext();
  const { isDualPriceForCroatia } = useIsDualPriceForCroatia();
  const newDesign = useNewPdDesignEnabled();
  const isOnStock =
    item.availability.stockAvailability !== StockAvailability.OutOfStock;

  const handleClick = () => {
    const attributes = Object.keys(getRenderableAttributes(item.attributes));
    trackVariantClick(
      getTimeFromInit(),
      VariantDisplayTypes.Tiles,
      isOnStock ? attributes?.[0] : undefined
    );
  };

  return (
    <VariantListItem minWidth={newDesign ? '0px' : undefined}>
      <ErrorBoundary>
        <VariantLink
          onClick={handleClick}
          to={item.url}
          replace={true}
          id={`pd-variant-${item.webId}`}
          className={isSelected ? 'pd-variant-selected' : ''}
          {...dataLabelsProp(item.attributes)}
        >
          <Variant
            isOnStock={isOnStock}
            newDesign={newDesign}
            isDualPriceForCroatia={isDualPriceForCroatia}
            isSelected={isSelected}
          >
            <Tags
              onClick={(attr) => trackTagClick(attr, getTimeFromInit())}
              type={TagModel.Types.corner}
              small={newDesign}
              attributes={item.attributes}
              stockAvailability={item.stockAvailability.code}
            />

            <Name
              newDesign={newDesign}
              isOnStock={isOnStock}
              className="pd-variant-label"
            >
              {`${item.parameters?.package || ''}\u00A0${unit || ''}`}
            </Name>

            {isOnStock && (
              <PriceLabel
                price={item.price}
                wrapperComponent={(additionalProps) => (
                  <PriceLabelWrapper
                    newDesign={newDesign}
                    isSelected={isSelected}
                    isOnStock={isOnStock}
                    {...additionalProps}
                  />
                )}
                currencyComponent={PriceLabelCurrency}
              />
            )}

            {!isOnStock && newDesign && (
              <Unavailable>
                <FormattedMessage {...messages.unavailable} />
              </Unavailable>
            )}
          </Variant>
        </VariantLink>
      </ErrorBoundary>
    </VariantListItem>
  );
};
